'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { 
  Layout, 
  Move, 
  RotateCw, 
  ZoomIn, 
  Grid3X3, 
  Download,
  Settings,
  Eye,
  EyeOff
} from 'lucide-react';

interface CanvasElement {
  id: string;
  type: 'box' | 'sphere' | 'plane' | 'text';
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  color: string;
  visible: boolean;
}

interface CanvasProject {
  id: string;
  name: string;
  elements: CanvasElement[];
  camera: {
    position: [number, number, number];
    target: [number, number, number];
  };
}

const ArcCanvasWorkspace: React.FC = () => {
  const [activeProject, setActiveProject] = useState<CanvasProject>({
    id: 'default',
    name: 'Support Flow Visualization',
    elements: [
      {
        id: 'element-1',
        type: 'box',
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        scale: [1, 1, 1],
        color: '#3b82f6',
        visible: true
      },
      {
        id: 'element-2', 
        type: 'sphere',
        position: [2, 0, 0],
        rotation: [0, 0, 0],
        scale: [0.8, 0.8, 0.8],
        color: '#10b981',
        visible: true
      },
      {
        id: 'element-3',
        type: 'box',
        position: [-2, 0, 0],
        rotation: [0, Math.PI / 4, 0],
        scale: [0.6, 1.2, 0.6],
        color: '#f59e0b',
        visible: true
      }
    ],
    camera: {
      position: [5, 5, 5],
      target: [0, 0, 0]
    }
  });

  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [tool, setTool] = useState<'select' | 'move' | 'rotate' | 'scale'>('select');
  const [showGrid, setShowGrid] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);

  const handleElementClick = useCallback((elementId: string) => {
    setSelectedElement(elementId);
  }, []);

  const handleAddElement = async (type: 'box' | 'sphere' | 'plane') => {
    setIsProcessing(true);
    
    try {
      const response = await fetch('/api/arccanvas/layout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'layout',
          sessionId: `canvas-${Date.now()}`,
          designSpecs: {
            elements: activeProject.elements.length + 1,
            type: 'add-element',
            elementType: type
          }
        })
      });
      
      const result = await response.json();
      
      if (result.status === 'done') {
        const newElement: CanvasElement = {
          id: `element-${Date.now()}`,
          type,
          position: [Math.random() * 4 - 2, 0, Math.random() * 4 - 2],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          color: 'var(--tyler-blue)',
          visible: true
        };
        
        setActiveProject(prev => ({
          ...prev,
          elements: [...prev.elements, newElement]
        }));
      }
    } catch (error) {
      console.error('Failed to add element:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOptimizeLayout = async () => {
    setIsProcessing(true);
    
    try {
      const response = await fetch('/api/arccanvas/optimize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'optimize',
          sessionId: `canvas-${Date.now()}`,
          canvasData: {
            elements: activeProject.elements.map(el => ({
              id: el.id,
              type: el.type,
              x: el.position[0],
              y: el.position[1],
              width: el.scale[0],
              height: el.scale[1]
            }))
          }
        })
      });
      
      const result = await response.json();
      console.log('Layout optimized:', result);
    } catch (error) {
      console.error('Failed to optimize layout:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportCanvas = () => {
    const exportData = {
      project: activeProject,
      timestamp: new Date().toISOString(),
      format: 'arccanvas-project'
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeProject.name.replace(/\s+/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const toggleElementVisibility = (elementId: string) => {
    setActiveProject(prev => ({
      ...prev,
      elements: prev.elements.map(el => 
        el.id === elementId ? { ...el, visible: !el.visible } : el
      )
    }));
  };

  const CanvasElement3D: React.FC<{ element: CanvasElement; isSelected: boolean }> = ({ element, isSelected }) => {
    if (!element.visible) return null;

    const handleClick = (e: any) => {
      e.stopPropagation();
      handleElementClick(element.id);
    };

    const meshProps = {
      position: element.position,
      rotation: element.rotation,
      scale: element.scale,
      onClick: handleClick
    };

    return (
      <mesh {...meshProps}>
        {element.type === 'box' && <boxGeometry args={[1, 1, 1]} />}
        {element.type === 'sphere' && <sphereGeometry args={[1, 32, 32]} />}
        {element.type === 'plane' && <planeGeometry args={[1, 1]} />}
        <meshStandardMaterial 
          color={element.color} 
          wireframe={isSelected}
          transparent={isSelected}
          opacity={isSelected ? 0.7 : 1}
        />
        {isSelected && (
          <lineSegments>
            <edgesGeometry args={[element.type === 'box' ? new THREE.BoxGeometry(1, 1, 1) : new THREE.SphereGeometry(1, 8, 8)]} />
            <lineBasicMaterial color="#ffffff" />
          </lineSegments>
        )}
      </mesh>
    );
  };

  return (
    <div className="w-full h-full bg-slate-900 border border-gray-700 rounded-lg overflow-hidden">
      <div className="flex h-full">
        {/* Toolbar */}
        <div className="w-16 bg-slate-800 border-r border-gray-700 flex flex-col items-center py-4 space-y-2">
          {[
            { tool: 'select', icon: Layout, label: 'Select' },
            { tool: 'move', icon: Move, label: 'Move' },
            { tool: 'rotate', icon: RotateCw, label: 'Rotate' },
            { tool: 'scale', icon: ZoomIn, label: 'Scale' }
          ].map(({ tool: toolType, icon: Icon, label }) => (
            <button
              key={toolType}
              onClick={() => setTool(toolType as any)}
              className={`p-2 rounded-lg transition-colors ${
                tool === toolType 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-slate-700'
              }`}
              title={label}
            >
              <Icon className="w-5 h-5" />
            </button>
          ))}
          
          <div className="border-t border-gray-700 pt-2 mt-2">
            <button
              onClick={() => setShowGrid(!showGrid)}
              className={`p-2 rounded-lg transition-colors ${
                showGrid 
                  ? 'bg-green-600 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-slate-700'
              }`}
              title="Toggle Grid"
            >
              <Grid3X3 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Canvas */}
        <div className="flex-1 flex flex-col">
          {/* Top Bar */}
          <div className="h-12 bg-slate-800 border-b border-gray-700 flex items-center justify-between px-4">
            <div className="flex items-center space-x-4">
              <h3 className="text-sm font-semibold text-white">{activeProject.name}</h3>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => handleAddElement('box')}
                  disabled={isProcessing}
                  className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  + Box
                </button>
                <button 
                  onClick={() => handleAddElement('sphere')}
                  disabled={isProcessing}
                  className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  + Sphere
                </button>
                <button 
                  onClick={handleOptimizeLayout}
                  disabled={isProcessing}
                  className="text-xs px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  Optimize
                </button>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button 
                onClick={handleExportCanvas}
                className="text-gray-400 hover:text-white"
                title="Export Canvas"
              >
                <Download className="w-4 h-4" />
              </button>
              <button className="text-gray-400 hover:text-white" title="Settings">
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* 3D Canvas */}
          <div className="flex-1 relative" ref={canvasRef}>
            <Canvas camera={{ position: activeProject.camera.position, fov: 75 }}>
              <ambientLight intensity={0.4} />
              <directionalLight position={[10, 10, 5]} intensity={1} />
              <pointLight position={[-10, -10, -10]} intensity={0.3} />
              
              <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
              
              {showGrid && (
                <gridHelper args={[10, 10, '#374151', '#374151']} />
              )}
              
              {activeProject.elements.map((element) => (
                <CanvasElement3D
                  key={element.id}
                  element={element}
                  isSelected={selectedElement === element.id}
                />
              ))}
            </Canvas>
            
            {isProcessing && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="bg-slate-800 p-4 rounded-lg">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                  <p className="text-white text-sm">Processing...</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Properties Panel */}
        <div className="w-64 bg-slate-800 border-l border-gray-700 flex flex-col">
          <div className="p-4 border-b border-gray-700">
            <h4 className="text-sm font-semibold text-white">Elements</h4>
          </div>
          
          <div className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-2">
              {activeProject.elements.map((element) => (
                <div
                  key={element.id}
                  className={`p-2 rounded-lg cursor-pointer transition-colors ${
                    selectedElement === element.id 
                      ? 'bg-blue-600/20 border border-blue-500/30' 
                      : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                  onClick={() => setSelectedElement(element.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: element.color }}
                      />
                      <span className="text-xs text-white">{element.type}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleElementVisibility(element.id);
                      }}
                      className="text-gray-400 hover:text-white"
                    >
                      {element.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    </button>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {element.position.map(p => p.toFixed(1)).join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArcCanvasWorkspace;
